/**
 * Hook: Pipeline versioning — drift detection, new-version creation, and step replay.
 *
 * Converts per-step drift results into the existing SchemaWarning format so that
 * DAGNode visuals (red/amber borders, badges) work without any changes to DAGNode.tsx.
 */
import { useState, useCallback, useMemo } from 'react';
import type { SchemaWarning } from '../DAGNode';
import {
  prepareNewVersion,
  createNewVersion,
  getPipelineDrift,
  replayPipelineStep,
} from '../../../services/workspaceApi';
import type {
  PipelineDriftReport,
  PrepareNewVersionResult,
  NewVersionResult,
  ReplayStepResult,
  StepDriftResult,
} from '../../../services/workspaceApi';

export interface UsePipelineVersioningArgs {
  sessionId: string;
}

export function usePipelineVersioning({ sessionId }: UsePipelineVersioningArgs) {
  const [driftReport, setDriftReport] = useState<PipelineDriftReport | null>(null);
  const [preparing, setPreparing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [replaying, setReplaying] = useState<string | null>(null); // step_id being replayed

  // Convert drift results to SchemaWarning map for DAGNode rendering.
  // Key is step_id (matches operation_id in the DAG's warning lookup).
  const driftWarnings = useMemo(() => {
    const warnings = new Map<string, SchemaWarning>();
    if (!driftReport) return warnings;

    for (const [stepId, result] of Object.entries(driftReport.results)) {
      if (result.status === 'red') {
        warnings.set(stepId, {
          operationId: stepId,
          severity: 'error',
          message: result.reason,
          brokenColumns: result.column_drifts
            .filter((d) => d.change_class === 'breaking')
            .map((d) => d.column),
        });
      } else if (result.status === 'amber') {
        warnings.set(stepId, {
          operationId: stepId,
          severity: 'warning',
          message: result.reason,
        });
      }
      // GREEN → no warning entry
    }
    return warnings;
  }, [driftReport]);

  const prepare = useCallback(
    async (sourcePipelineId?: string): Promise<PrepareNewVersionResult> => {
      setPreparing(true);
      try {
        return await prepareNewVersion(sessionId, sourcePipelineId);
      } finally {
        setPreparing(false);
      }
    },
    [sessionId],
  );

  const create = useCallback(
    async (
      sourceTableMapping: Record<string, string>,
      sourcePipelineId?: string,
    ): Promise<NewVersionResult> => {
      setCreating(true);
      try {
        const result = await createNewVersion(sessionId, sourceTableMapping, sourcePipelineId);
        setDriftReport(result.drift_report);
        return result;
      } finally {
        setCreating(false);
      }
    },
    [sessionId],
  );

  const replay = useCallback(
    async (pipelineId: string, stepId: string): Promise<ReplayStepResult> => {
      setReplaying(stepId);
      try {
        const result = await replayPipelineStep(sessionId, pipelineId, stepId);
        if (result.drift_report) {
          setDriftReport(result.drift_report);
        }
        return result;
      } finally {
        setReplaying(null);
      }
    },
    [sessionId],
  );

  const loadDriftReport = useCallback(
    async (pipelineId: string) => {
      try {
        const report = await getPipelineDrift(sessionId, pipelineId);
        setDriftReport(report);
      } catch {
        // No drift report yet — normal for non-versioned pipelines
        setDriftReport(null);
      }
    },
    [sessionId],
  );

  const clearDriftReport = useCallback(() => setDriftReport(null), []);

  // Get drift result for a specific step
  const getStepDrift = useCallback(
    (stepId: string): StepDriftResult | null => {
      return driftReport?.results[stepId] ?? null;
    },
    [driftReport],
  );

  return {
    driftReport,
    driftWarnings,
    preparing,
    creating,
    replaying,
    prepare,
    create,
    replay,
    loadDriftReport,
    clearDriftReport,
    getStepDrift,
  };
}
